import { useUrls } from "@/hooks/useUrls";
import styles from "./styles.module.scss";
import * as Dialog from "@radix-ui/react-dialog";
import { EndNotes } from "@/components/EndNotes";

interface MenuProps {
  children: React.ReactNode;
  className?: string;
  "data-layer-region": string;
  "data-layer-text": string;
}

export function Menu({
  children,
  className,
  "data-layer-region": dataLayerRegion,
  "data-layer-text": dataLayerText,
}: MenuProps) {
  const urls = useUrls();

  return (
    <Dialog.Root>
      <Dialog.Trigger
        className={className}
        data-layer-event-prefix="UI Item"
        data-layer-action="click"
        data-layer-region={dataLayerRegion}
        data-layer-text={dataLayerText}
      >
        {children}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className={styles["overlay"]} />
        <Dialog.Content className={styles["menu"]}>
          <div className={styles["menu-inner"]}>
            <div className={styles["menu-header"]}>
              <Dialog.Close
                className={styles["menu-close"]}
                data-layer-event-prefix="UI Item"
                data-layer-action="click"
                data-layer-text="close"
                data-layer-region="modal window"
              >
                <span>Close</span>
              </Dialog.Close>
            </div>

            <h2 className={styles["menu-title"]}>Global Trade Explorer</h2>

            <ul className={styles["menu-list"]}>
              {Object.values(urls).map((obj, i) => (
                <li key={i} className={styles["menu-item"]}>
                  <a
                    href={obj.url}
                    className={styles["menu-item-link"]}
                    data-layer-event-prefix="Navigation Link"
                    data-layer-action="click"
                    data-layer-text={obj.title}
                    data-layer-region="modal window"
                  >
                    <div className={styles["menu-item-img-wrap"]}>
                      <img
                        className={styles["menu-item-img"]}
                        src={obj.thumbnailDark}
                        alt={obj.title}
                      />
                    </div>
                    <h3 className={styles["menu-item-headline"]}>
                      {obj.headline}
                    </h3>
                    <p className={styles["menu-item-title"]}>{obj.title}</p>
                  </a>
                </li>
              ))}
            </ul>

            <div className={styles["menu-footer"]}>
              <div className={styles["menu-footer-cta"]}>
                <p>
                  {" "}
                  Learn how global connections and the flow of goods shape
                  economies.{" "}
                  <a
                    href="https://www.mckinsey.com/mgi/our-research/global-connections"
                    data-layer-event-prefix="Navigation Link"
                    data-layer-text="Explore relevant MGI research"
                    data-layer-region="modal window"
                  >
                    Explore relevant MGI research
                  </a>
                  .
                </p>
              </div>

              <div className={styles["menu-footer-notes"]}>
                <EndNotes mode="no-heading" isDarkBG={true}>
                  <p>
                    Trade values shown are based on self-reported trade values
                    to UN Comtrade. The accuracy of the data is based on each
                    economy’s reported values. Sector taxonomy and
                    classification are based on the judgment of several industry
                    experts working with McKinsey Global Institute. There are
                    asymmetries in UN Comtrade import and export datasets. See
                    OECD BIMTS for further details. Data used in the
                    visualization is from or ends in 2023. McKinsey & Company
                    expects to update the data as 2024 data becomes available
                    but is under no obligation to update the data or correct the
                    results presented in this visualization.
                  </p>
                </EndNotes>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
